/**
 * ====================================================================
 * FIKO INTERACTIVE ARROW SYSTEM
 * ====================================================================
 *
 * Gelişmiş, interaktif ok sistemi.
 * Kameraya göre otomatik yönlenme, hover/select animasyonları,
 * çoklu ok stilleri ve görsel efektler.
 *
 * @author FikoYT CAD Team
 * @version 3.0.0 - NEXT GEN
 */

import * as THREE from 'three';
import { FikoSahne } from '../../../FikoSahne';

// ====================================================================
// TYPES & ENUMS
// ====================================================================

/**
 * Ok stilleri
 */
export enum ArrowStyleType {
    // Klasik
    FILLED_TRIANGLE = 'FILLED_TRIANGLE',
    OPEN_TRIANGLE = 'OPEN_TRIANGLE',
    DOT = 'DOT',
    TICK = 'TICK',

    // Profesyonel CAD
    CATIA_STANDARD = 'CATIA_STANDARD',
    SOLIDWORKS_STANDARD = 'SOLIDWORKS_STANDARD',
    AUTOCAD_STANDARD = 'AUTOCAD_STANDARD',

    // Futuristik
    HOLOGRAPHIC = 'HOLOGRAPHIC',
    NEON_GLOW = 'NEON_GLOW',
    PLASMA_CORE = 'PLASMA_CORE',
    ENERGY_BEAM = 'ENERGY_BEAM',
    LASER_POINT = 'LASER_POINT',

    // Geometrik
    DIAMOND = 'DIAMOND',
    HEXAGON = 'HEXAGON',
    STAR_5 = 'STAR_5',
    STAR_6 = 'STAR_6',
    CROSS = 'CROSS',
    PLUS = 'PLUS',

    // Özel
    CHEVRON_SINGLE = 'CHEVRON_SINGLE',
    CHEVRON_DOUBLE = 'CHEVRON_DOUBLE',
    CHEVRON_TRIPLE = 'CHEVRON_TRIPLE',
    ARROW_DOUBLE = 'ARROW_DOUBLE',
    ARROW_TRIPLE = 'ARROW_TRIPLE',

    // Mimari
    ARCHITECTURAL_TICK = 'ARCHITECTURAL_TICK',
    ARCHITECTURAL_DOT = 'ARCHITECTURAL_DOT',
    DATUM_TRIANGLE = 'DATUM_TRIANGLE',
    ORIGIN_MARK = 'ORIGIN_MARK',

    // Animasyonlu
    PULSING_RING = 'PULSING_RING',
    ROTATING_STAR = 'ROTATING_STAR',
    BREATHING_CORE = 'BREATHING_CORE',

    NONE = 'NONE'
}

/**
 * Ok durumu
 */
export enum ArrowState {
    IDLE = 'IDLE',
    HOVER = 'HOVER',
    SELECTED = 'SELECTED',
    DRAGGING = 'DRAGGING',
    ANIMATING = 'ANIMATING',
    DISABLED = 'DISABLED'
}

/**
 * Ok yönü
 */
export enum ArrowDirection {
    FORWARD = 'FORWARD',    // +X yönünde
    BACKWARD = 'BACKWARD',  // -X yönünde
    BOTH = 'BOTH'           // Her iki yönde
}

/**
 * Ok animasyon tipi
 */
export enum ArrowAnimation {
    NONE = 'NONE',
    PULSE = 'PULSE',
    GLOW = 'GLOW',
    ROTATE = 'ROTATE',
    BOUNCE = 'BOUNCE',
    WAVE = 'WAVE',
    SPARKLE = 'SPARKLE',
    ELECTRIC = 'ELECTRIC',
    BREATHING = 'BREATHING'
}

/**
 * Ok renk paleti
 */
export interface ArrowColorPalette {
    primary: number;
    secondary: number;
    glow: number;
    hover: number;
    selected: number;
    disabled: number;
}

/**
 * Ok ayarları
 */
export interface ArrowOptions {
    style: ArrowStyleType;
    size: number;
    aspectRatio: number;
    colors: Partial<ArrowColorPalette>;
    animation: ArrowAnimation;
    animationSpeed: number;
    animationIntensity: number;
    glowEnabled: boolean;
    glowIntensity: number;
    glowRadius: number;
    outlineEnabled: boolean;
    outlineWidth: number;
    outlineColor: number;
    depthTest: boolean;
    opacity: number;
    castShadow: boolean;
    receiveShadow: boolean;
    alwaysFaceCamera: boolean;
    autoScale: boolean;       // Kamera mesafesine göre otomatik ölçekle
    minScale: number;
    maxScale: number;
    interactable: boolean;
}

// ====================================================================
// DEFAULT VALUES
// ====================================================================

export const DEFAULT_ARROW_COLORS: ArrowColorPalette = {
    primary: 0x00ffff,
    secondary: 0xff00ff,
    glow: 0x00ffff,
    hover: 0x00ff00,
    selected: 0xff6600,
    disabled: 0x666666
};

export const DEFAULT_ARROW_OPTIONS: ArrowOptions = {
    style: ArrowStyleType.HOLOGRAPHIC,
    size: 1,
    aspectRatio: 2.5,
    colors: DEFAULT_ARROW_COLORS,
    animation: ArrowAnimation.PULSE,
    animationSpeed: 1,
    animationIntensity: 0.5,
    glowEnabled: true,
    glowIntensity: 0.7,
    glowRadius: 1.5,
    outlineEnabled: false,
    outlineWidth: 0.1,
    outlineColor: 0xffffff,
    depthTest: false,
    opacity: 1,
    castShadow: false,
    receiveShadow: false,
    alwaysFaceCamera: false,
    autoScale: true,
    minScale: 0.5,
    maxScale: 2,
    interactable: true
};

// ====================================================================
// MAIN CLASS
// ====================================================================

/**
 * FikoInteractiveArrow - Gelişmiş interaktif ok sistemi
 */
export class FikoInteractiveArrow extends THREE.Group {
    readonly type = 'FikoInteractiveArrow';

    // Ayarlar
    protected options: ArrowOptions;
    protected colors: ArrowColorPalette;

    // Durum
    protected _state: ArrowState = ArrowState.IDLE;
    protected _direction: THREE.Vector3 = new THREE.Vector3(1, 0, 0);
    protected _targetDirection: THREE.Vector3 = new THREE.Vector3(1, 0, 0);

    // Sahne referansı
    protected scene: FikoSahne | null = null;

    // Çizim elemanları
    protected mainMesh: THREE.Mesh | null = null;
    protected glowMesh: THREE.Mesh | null = null;
    protected outlineMesh: THREE.Mesh | null = null;
    protected effectGroup: THREE.Group | null = null;

    // Animasyon
    protected animationFrame: number | null = null;
    protected animationTime: number = 0;

    // Shader materials
    protected glowShaderMaterial: THREE.ShaderMaterial | null = null;

    // Event handlers
    protected onHoverCallback: ((arrow: FikoInteractiveArrow) => void) | null = null;
    protected onSelectCallback: ((arrow: FikoInteractiveArrow) => void) | null = null;
    protected onClickCallback: ((arrow: FikoInteractiveArrow) => void) | null = null;

    // Performans için static buffer'lar
    private static readonly _tempVec1 = new THREE.Vector3();
    private static readonly _tempVec2 = new THREE.Vector3();
    private static readonly _tempQuat = new THREE.Quaternion();

    // Shared geometries (memory optimization)
    private static geometryCache = new Map<string, THREE.BufferGeometry>();

    // ====================================================================
    // CONSTRUCTOR
    // ====================================================================

    constructor(options: Partial<ArrowOptions> = {}) {
        super();

        // Ayarları birleştir
        this.options = { ...DEFAULT_ARROW_OPTIONS, ...options };
        this.colors = { ...DEFAULT_ARROW_COLORS, ...options.colors };

        // Oluştur
        this.build();

        // Animasyonu başlat
        if (this.options.animation !== ArrowAnimation.NONE) {
            this.startAnimation();
        }
    }

    // ====================================================================
    // GETTERS & SETTERS
    // ====================================================================

    get state(): ArrowState {
        return this._state;
    }

    set state(value: ArrowState) {
        if (this._state !== value) {
            const oldState = this._state;
            this._state = value;
            this.onStateChange(oldState, value);
        }
    }

    get direction(): THREE.Vector3 {
        return this._direction.clone();
    }

    set direction(value: THREE.Vector3) {
        this._targetDirection.copy(value).normalize();
        this.updateDirection();
    }

    // ====================================================================
    // BUILD METHODS
    // ====================================================================

    /**
     * Ana yapıyı oluştur
     */
    protected build(): void {
        this.clearGeometry();

        // Ana ok mesh'i oluştur
        this.buildMainMesh();

        // Glow efekti
        if (this.options.glowEnabled) {
            this.buildGlowMesh();
        }

        // Outline
        if (this.options.outlineEnabled) {
            this.buildOutlineMesh();
        }

        // Efekt grubu
        this.buildEffectGroup();
    }

    /**
     * Ana mesh'i oluştur
     */
    protected buildMainMesh(): void {
        const geometry = this.createArrowGeometry(this.options.style);
        const material = this.createArrowMaterial();

        this.mainMesh = new THREE.Mesh(geometry, material);
        this.mainMesh.renderOrder = 10001;
        this.mainMesh.frustumCulled = false;

        this.add(this.mainMesh);
    }

    /**
     * Ok geometrisi oluştur
     */
    protected createArrowGeometry(style: ArrowStyleType): THREE.BufferGeometry {
        const cacheKey = `${style}_${this.options.size}_${this.options.aspectRatio}`;

        if (FikoInteractiveArrow.geometryCache.has(cacheKey)) {
            return FikoInteractiveArrow.geometryCache.get(cacheKey)!.clone();
        }

        let geometry: THREE.BufferGeometry;

        switch (style) {
            case ArrowStyleType.FILLED_TRIANGLE:
                geometry = this.createFilledTriangleGeometry();
                break;
            case ArrowStyleType.OPEN_TRIANGLE:
                geometry = this.createOpenTriangleGeometry();
                break;
            case ArrowStyleType.DOT:
                geometry = this.createDotGeometry();
                break;
            case ArrowStyleType.TICK:
                geometry = this.createTickGeometry();
                break;
            case ArrowStyleType.HOLOGRAPHIC:
                geometry = this.createHolographicGeometry();
                break;
            case ArrowStyleType.NEON_GLOW:
                geometry = this.createNeonGeometry();
                break;
            case ArrowStyleType.PLASMA_CORE:
                geometry = this.createPlasmaGeometry();
                break;
            case ArrowStyleType.ENERGY_BEAM:
                geometry = this.createEnergyBeamGeometry();
                break;
            case ArrowStyleType.LASER_POINT:
                geometry = this.createLaserPointGeometry();
                break;
            case ArrowStyleType.DIAMOND:
                geometry = this.createDiamondGeometry();
                break;
            case ArrowStyleType.HEXAGON:
                geometry = this.createHexagonGeometry();
                break;
            case ArrowStyleType.STAR_5:
                geometry = this.createStarGeometry(5);
                break;
            case ArrowStyleType.STAR_6:
                geometry = this.createStarGeometry(6);
                break;
            case ArrowStyleType.CROSS:
                geometry = this.createCrossGeometry();
                break;
            case ArrowStyleType.PLUS:
                geometry = this.createPlusGeometry();
                break;
            case ArrowStyleType.CHEVRON_SINGLE:
                geometry = this.createChevronGeometry(1);
                break;
            case ArrowStyleType.CHEVRON_DOUBLE:
                geometry = this.createChevronGeometry(2);
                break;
            case ArrowStyleType.CHEVRON_TRIPLE:
                geometry = this.createChevronGeometry(3);
                break;
            case ArrowStyleType.ARROW_DOUBLE:
                geometry = this.createMultiArrowGeometry(2);
                break;
            case ArrowStyleType.ARROW_TRIPLE:
                geometry = this.createMultiArrowGeometry(3);
                break;
            case ArrowStyleType.DATUM_TRIANGLE:
                geometry = this.createDatumTriangleGeometry();
                break;
            case ArrowStyleType.ORIGIN_MARK:
                geometry = this.createOriginMarkGeometry();
                break;
            case ArrowStyleType.PULSING_RING:
                geometry = this.createPulsingRingGeometry();
                break;
            case ArrowStyleType.ROTATING_STAR:
                geometry = this.createRotatingStarGeometry();
                break;
            case ArrowStyleType.CATIA_STANDARD:
            case ArrowStyleType.SOLIDWORKS_STANDARD:
            case ArrowStyleType.AUTOCAD_STANDARD:
            default:
                geometry = this.createFilledTriangleGeometry();
                break;
        }

        // Cache'e ekle
        FikoInteractiveArrow.geometryCache.set(cacheKey, geometry);

        return geometry.clone();
    }

    /**
     * Dolu üçgen geometri
     */
    protected createFilledTriangleGeometry(): THREE.BufferGeometry {
        const { size, aspectRatio } = this.options;
        const length = size;
        const width = size / aspectRatio;

        const shape = new THREE.Shape();
        shape.moveTo(length, 0);
        shape.lineTo(-length * 0.2, width / 2);
        shape.lineTo(-length * 0.1, 0);
        shape.lineTo(-length * 0.2, -width / 2);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        return geometry;
    }

    /**
     * Açık üçgen geometri
     */
    protected createOpenTriangleGeometry(): THREE.BufferGeometry {
        const { size, aspectRatio } = this.options;
        const length = size;
        const width = size / aspectRatio;
        const thickness = size * 0.1;

        const shape = new THREE.Shape();

        // Dış V
        shape.moveTo(length, 0);
        shape.lineTo(-length * 0.2, width / 2);
        shape.lineTo(-length * 0.1, width / 2 - thickness);
        shape.lineTo(length - thickness * 2, 0);
        shape.lineTo(-length * 0.1, -width / 2 + thickness);
        shape.lineTo(-length * 0.2, -width / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Nokta geometri
     */
    protected createDotGeometry(): THREE.BufferGeometry {
        return new THREE.CircleGeometry(this.options.size * 0.3, 24);
    }

    /**
     * Tick geometri (45 derece çizgi)
     */
    protected createTickGeometry(): THREE.BufferGeometry {
        const { size } = this.options;
        const length = size * 0.8;
        const thickness = size * 0.1;

        const shape = new THREE.Shape();
        const cos45 = Math.SQRT1_2;
        const sin45 = Math.SQRT1_2;
        const halfLen = length / 2;
        const halfThick = thickness / 2;

        shape.moveTo(-halfLen * cos45 - halfThick * sin45, -halfLen * sin45 + halfThick * cos45);
        shape.lineTo(halfLen * cos45 - halfThick * sin45, halfLen * sin45 + halfThick * cos45);
        shape.lineTo(halfLen * cos45 + halfThick * sin45, halfLen * sin45 - halfThick * cos45);
        shape.lineTo(-halfLen * cos45 + halfThick * sin45, -halfLen * sin45 - halfThick * cos45);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Holografik geometri
     */
    protected createHolographicGeometry(): THREE.BufferGeometry {
        const { size, aspectRatio } = this.options;

        // Ana üçgen
        const length = size;
        const width = size / aspectRatio;

        const shape = new THREE.Shape();
        shape.moveTo(length, 0);
        shape.lineTo(-length * 0.15, width / 2);
        shape.lineTo(-length * 0.05, 0);
        shape.lineTo(-length * 0.15, -width / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Neon geometri
     */
    protected createNeonGeometry(): THREE.BufferGeometry {
        return this.createOpenTriangleGeometry();
    }

    /**
     * Plazma geometri
     */
    protected createPlasmaGeometry(): THREE.BufferGeometry {
        // Merkez küre + dış halka
        const geometry = new THREE.SphereGeometry(this.options.size * 0.25, 16, 16);
        return geometry;
    }

    /**
     * Enerji ışını geometri
     */
    protected createEnergyBeamGeometry(): THREE.BufferGeometry {
        const { size } = this.options;

        const shape = new THREE.Shape();
        shape.moveTo(size, 0);
        shape.lineTo(size * 0.3, size * 0.15);
        shape.lineTo(size * 0.5, size * 0.05);
        shape.lineTo(-size * 0.2, size * 0.2);
        shape.lineTo(size * 0.1, 0);
        shape.lineTo(-size * 0.2, -size * 0.2);
        shape.lineTo(size * 0.5, -size * 0.05);
        shape.lineTo(size * 0.3, -size * 0.15);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Lazer nokta geometri
     */
    protected createLaserPointGeometry(): THREE.BufferGeometry {
        // İç içe daireler
        const outerGeometry = new THREE.RingGeometry(
            this.options.size * 0.25,
            this.options.size * 0.35,
            24
        );
        return outerGeometry;
    }

    /**
     * Elmas geometri
     */
    protected createDiamondGeometry(): THREE.BufferGeometry {
        const { size } = this.options;

        const shape = new THREE.Shape();
        shape.moveTo(size, 0);
        shape.lineTo(0, size * 0.4);
        shape.lineTo(-size * 0.4, 0);
        shape.lineTo(0, -size * 0.4);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Altıgen geometri
     */
    protected createHexagonGeometry(): THREE.BufferGeometry {
        const { size } = this.options;
        const radius = size * 0.4;

        const shape = new THREE.Shape();
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI * 2) / 6 - Math.PI / 6;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            if (i === 0) {
                shape.moveTo(x, y);
            } else {
                shape.lineTo(x, y);
            }
        }
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Yıldız geometri
     */
    protected createStarGeometry(points: number): THREE.BufferGeometry {
        const { size } = this.options;
        const outerRadius = size * 0.4;
        const innerRadius = size * 0.2;

        const shape = new THREE.Shape();
        for (let i = 0; i < points * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i * Math.PI) / points - Math.PI / 2;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            if (i === 0) {
                shape.moveTo(x, y);
            } else {
                shape.lineTo(x, y);
            }
        }
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Artı geometri
     */
    protected createCrossGeometry(): THREE.BufferGeometry {
        const { size } = this.options;
        const arm = size * 0.4;
        const thickness = size * 0.15;

        const shape = new THREE.Shape();
        // Üst
        shape.moveTo(-thickness, arm);
        shape.lineTo(thickness, arm);
        // Sağ üst köşe
        shape.lineTo(thickness, thickness);
        shape.lineTo(arm, thickness);
        // Sağ
        shape.lineTo(arm, -thickness);
        // Sağ alt köşe
        shape.lineTo(thickness, -thickness);
        shape.lineTo(thickness, -arm);
        // Alt
        shape.lineTo(-thickness, -arm);
        // Sol alt köşe
        shape.lineTo(-thickness, -thickness);
        shape.lineTo(-arm, -thickness);
        // Sol
        shape.lineTo(-arm, thickness);
        // Sol üst köşe
        shape.lineTo(-thickness, thickness);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Plus geometri
     */
    protected createPlusGeometry(): THREE.BufferGeometry {
        return this.createCrossGeometry();
    }

    /**
     * Chevron geometri
     */
    protected createChevronGeometry(count: number): THREE.BufferGeometry {
        const { size } = this.options;
        const w = size * 0.3;
        const h = size;
        const gap = size * 0.3;

        const geometries: THREE.BufferGeometry[] = [];

        for (let i = 0; i < count; i++) {
            const offset = -i * gap;
            const shape = new THREE.Shape();

            shape.moveTo(h + offset, 0);
            shape.lineTo(h * 0.3 + offset, w);
            shape.lineTo(h * 0.5 + offset, w);
            shape.lineTo(h + offset - size * 0.15, 0);
            shape.lineTo(h * 0.5 + offset, -w);
            shape.lineTo(h * 0.3 + offset, -w);
            shape.closePath();

            geometries.push(new THREE.ShapeGeometry(shape));
        }

        return this.mergeGeometries(geometries);
    }

    /**
     * Çoklu ok geometri
     */
    protected createMultiArrowGeometry(count: number): THREE.BufferGeometry {
        const { size, aspectRatio } = this.options;
        const length = size * 0.6;
        const width = size / aspectRatio;
        const gap = size * 0.4;

        const geometries: THREE.BufferGeometry[] = [];

        for (let i = 0; i < count; i++) {
            const offset = -i * gap;
            const shape = new THREE.Shape();

            shape.moveTo(length + offset, 0);
            shape.lineTo(offset, width / 2);
            shape.lineTo(length * 0.3 + offset, 0);
            shape.lineTo(offset, -width / 2);
            shape.closePath();

            geometries.push(new THREE.ShapeGeometry(shape));
        }

        return this.mergeGeometries(geometries);
    }

    /**
     * Datum üçgen geometri
     */
    protected createDatumTriangleGeometry(): THREE.BufferGeometry {
        const { size } = this.options;
        const height = size;
        const width = size * 0.8;

        const shape = new THREE.Shape();
        shape.moveTo(0, height / 2);
        shape.lineTo(width / 2, -height / 2);
        shape.lineTo(-width / 2, -height / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Origin işareti geometri
     */
    protected createOriginMarkGeometry(): THREE.BufferGeometry {
        const { size } = this.options;
        const outerRadius = size * 0.4;
        const innerRadius = size * 0.2;

        const outer = new THREE.RingGeometry(innerRadius, outerRadius, 24);
        const center = new THREE.CircleGeometry(innerRadius * 0.5, 16);

        return this.mergeGeometries([outer, center]);
    }

    /**
     * Pulse ring geometri
     */
    protected createPulsingRingGeometry(): THREE.BufferGeometry {
        const { size } = this.options;
        return new THREE.RingGeometry(size * 0.3, size * 0.4, 32);
    }

    /**
     * Dönen yıldız geometri
     */
    protected createRotatingStarGeometry(): THREE.BufferGeometry {
        return this.createStarGeometry(6);
    }

    /**
     * Geometrileri birleştir
     */
    protected mergeGeometries(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
        let totalVertices = 0;
        let totalIndices = 0;

        for (const geo of geometries) {
            const position = geo.getAttribute('position');
            if (position) totalVertices += position.count;
            const index = geo.getIndex();
            if (index) totalIndices += index.count;
        }

        const positions = new Float32Array(totalVertices * 3);
        const uvs = new Float32Array(totalVertices * 2);
        const indices: number[] = [];

        let vertexOffset = 0;

        for (const geo of geometries) {
            const position = geo.getAttribute('position');
            const uv = geo.getAttribute('uv');
            const index = geo.getIndex();

            if (position) {
                for (let i = 0; i < position.count; i++) {
                    positions[(vertexOffset + i) * 3] = position.getX(i);
                    positions[(vertexOffset + i) * 3 + 1] = position.getY(i);
                    positions[(vertexOffset + i) * 3 + 2] = position.getZ(i);
                }
            }

            if (uv) {
                for (let i = 0; i < uv.count; i++) {
                    uvs[(vertexOffset + i) * 2] = uv.getX(i);
                    uvs[(vertexOffset + i) * 2 + 1] = uv.getY(i);
                }
            }

            if (index) {
                for (let i = 0; i < index.count; i++) {
                    indices.push(index.getX(i) + vertexOffset);
                }
            }

            if (position) vertexOffset += position.count;
        }

        const merged = new THREE.BufferGeometry();
        merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        merged.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
        if (indices.length > 0) {
            merged.setIndex(indices);
        }
        merged.computeVertexNormals();

        return merged;
    }

    /**
     * Ok materyali oluştur
     */
    protected createArrowMaterial(): THREE.MeshBasicMaterial {
        return new THREE.MeshBasicMaterial({
            color: this.colors.primary,
            transparent: true,
            opacity: this.options.opacity,
            side: THREE.DoubleSide,
            depthTest: this.options.depthTest,
            depthWrite: false
        });
    }

    /**
     * Glow mesh oluştur
     */
    protected buildGlowMesh(): void {
        if (!this.mainMesh) return;

        const geometry = this.mainMesh.geometry.clone();

        // Glow shader
        this.glowShaderMaterial = new THREE.ShaderMaterial({
            uniforms: {
                glowColor: { value: new THREE.Color(this.colors.glow) },
                intensity: { value: this.options.glowIntensity },
                time: { value: 0 }
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 glowColor;
                uniform float intensity;
                uniform float time;
                varying vec2 vUv;

                void main() {
                    float dist = length(vUv - 0.5) * 2.0;
                    float glow = exp(-dist * dist * 2.0);
                    float pulse = 1.0 + sin(time * 3.0) * 0.2;

                    gl_FragColor = vec4(glowColor, glow * intensity * pulse);
                }
            `,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide
        });

        this.glowMesh = new THREE.Mesh(geometry, this.glowShaderMaterial);
        this.glowMesh.scale.setScalar(this.options.glowRadius);
        this.glowMesh.renderOrder = 10000;
        this.glowMesh.frustumCulled = false;

        this.add(this.glowMesh);
    }

    /**
     * Outline mesh oluştur
     */
    protected buildOutlineMesh(): void {
        if (!this.mainMesh) return;

        const geometry = this.mainMesh.geometry.clone();
        const material = new THREE.MeshBasicMaterial({
            color: this.options.outlineColor,
            transparent: true,
            opacity: 0.8,
            side: THREE.BackSide,
            depthTest: false
        });

        this.outlineMesh = new THREE.Mesh(geometry, material);
        this.outlineMesh.scale.setScalar(1 + this.options.outlineWidth);
        this.outlineMesh.renderOrder = 9999;
        this.outlineMesh.frustumCulled = false;

        this.add(this.outlineMesh);
    }

    /**
     * Efekt grubu oluştur
     */
    protected buildEffectGroup(): void {
        this.effectGroup = new THREE.Group();
        this.effectGroup.name = 'EffectGroup';
        this.add(this.effectGroup);

        // Özel stil efektleri
        switch (this.options.style) {
            case ArrowStyleType.PLASMA_CORE:
                this.addPlasmaEffect();
                break;
            case ArrowStyleType.LASER_POINT:
                this.addLaserEffect();
                break;
            case ArrowStyleType.PULSING_RING:
                this.addPulsingRingEffect();
                break;
        }
    }

    /**
     * Plazma efekti ekle
     */
    protected addPlasmaEffect(): void {
        if (!this.effectGroup) return;

        // Dış halka
        const ringGeometry = new THREE.TorusGeometry(
            this.options.size * 0.35,
            this.options.size * 0.05,
            8,
            32
        );

        const ringMaterial = new THREE.MeshBasicMaterial({
            color: this.colors.secondary,
            transparent: true,
            opacity: 0.6,
            depthTest: false
        });

        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.rotation.x = Math.PI / 2;

        this.effectGroup.add(ring);
    }

    /**
     * Lazer efekti ekle
     */
    protected addLaserEffect(): void {
        if (!this.effectGroup) return;

        // Merkez nokta
        const dotGeometry = new THREE.CircleGeometry(this.options.size * 0.1, 16);
        const dotMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 1,
            depthTest: false
        });

        const dot = new THREE.Mesh(dotGeometry, dotMaterial);
        this.effectGroup.add(dot);
    }

    /**
     * Pulse ring efekti ekle
     */
    protected addPulsingRingEffect(): void {
        if (!this.effectGroup) return;

        // İkinci halka (animasyonlu)
        const ring2Geometry = new THREE.RingGeometry(
            this.options.size * 0.4,
            this.options.size * 0.5,
            32
        );

        const ring2Material = new THREE.MeshBasicMaterial({
            color: this.colors.glow,
            transparent: true,
            opacity: 0.3,
            depthTest: false
        });

        const ring2 = new THREE.Mesh(ring2Geometry, ring2Material);
        ring2.userData.isPulsingRing = true;

        this.effectGroup.add(ring2);
    }

    // ====================================================================
    // DIRECTION & POSITIONING
    // ====================================================================

    /**
     * Yönü güncelle
     */
    protected updateDirection(): void {
        const currentDir = FikoInteractiveArrow._tempVec1.copy(this._direction);
        const targetDir = FikoInteractiveArrow._tempVec2.copy(this._targetDirection);

        // Smooth interpolation
        currentDir.lerp(targetDir, 0.1);
        this._direction.copy(currentDir);

        // Quaternion hesapla
        const defaultDir = new THREE.Vector3(1, 0, 0);
        FikoInteractiveArrow._tempQuat.setFromUnitVectors(defaultDir, this._direction);

        this.quaternion.copy(FikoInteractiveArrow._tempQuat);
    }

    /**
     * Pozisyona ve yöne göre yerleştir
     */
    setPositionAndDirection(position: THREE.Vector3, direction: THREE.Vector3): void {
        this.position.copy(position);
        this._direction.copy(direction).normalize();
        this._targetDirection.copy(direction).normalize();

        const defaultDir = new THREE.Vector3(1, 0, 0);
        FikoInteractiveArrow._tempQuat.setFromUnitVectors(defaultDir, this._direction);
        this.quaternion.copy(FikoInteractiveArrow._tempQuat);
    }

    /**
     * Kameraya göre ölçekle
     */
    updateScaleForCamera(camera: THREE.Camera): void {
        if (!this.options.autoScale) return;

        const distance = camera.position.distanceTo(this.position);
        const baseScale = this.options.size;

        // Mesafeye göre ölçek (sabit ekran boyutu için)
        let scale = baseScale * (distance * 0.01);

        // Min/max sınırla
        scale = Math.max(this.options.minScale * baseScale, scale);
        scale = Math.min(this.options.maxScale * baseScale, scale);

        this.scale.setScalar(scale);
    }

    // ====================================================================
    // ANIMATION
    // ====================================================================

    /**
     * Animasyonu başlat
     */
    protected startAnimation(): void {
        if (this.animationFrame !== null) return;

        const animate = () => {
            this.animationTime += 0.016 * this.options.animationSpeed;
            this.updateAnimation();
            this.animationFrame = requestAnimationFrame(animate);
        };

        this.animationFrame = requestAnimationFrame(animate);
    }

    /**
     * Animasyonu durdur
     */
    stopAnimation(): void {
        if (this.animationFrame !== null) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    /**
     * Animasyonu güncelle
     */
    protected updateAnimation(): void {
        // Glow shader time güncelle
        if (this.glowShaderMaterial) {
            this.glowShaderMaterial.uniforms.time.value = this.animationTime;
        }

        switch (this.options.animation) {
            case ArrowAnimation.PULSE:
                this.updatePulseAnimation();
                break;
            case ArrowAnimation.GLOW:
                this.updateGlowAnimation();
                break;
            case ArrowAnimation.ROTATE:
                this.updateRotateAnimation();
                break;
            case ArrowAnimation.BOUNCE:
                this.updateBounceAnimation();
                break;
            case ArrowAnimation.WAVE:
                this.updateWaveAnimation();
                break;
            case ArrowAnimation.SPARKLE:
                this.updateSparkleAnimation();
                break;
            case ArrowAnimation.ELECTRIC:
                this.updateElectricAnimation();
                break;
            case ArrowAnimation.BREATHING:
                this.updateBreathingAnimation();
                break;
        }

        // Özel stil animasyonları
        this.updateStyleAnimation();
    }

    protected updatePulseAnimation(): void {
        const pulse = 1 + Math.sin(this.animationTime * 3) * this.options.animationIntensity * 0.2;
        if (this.mainMesh) {
            this.mainMesh.scale.setScalar(pulse);
        }
    }

    protected updateGlowAnimation(): void {
        if (this.glowMesh) {
            const glow = 1 + Math.sin(this.animationTime * 2) * this.options.animationIntensity * 0.3;
            this.glowMesh.scale.setScalar(this.options.glowRadius * glow);
        }
    }

    protected updateRotateAnimation(): void {
        if (this.mainMesh) {
            this.mainMesh.rotation.z = this.animationTime * this.options.animationIntensity;
        }
    }

    protected updateBounceAnimation(): void {
        const bounce = Math.abs(Math.sin(this.animationTime * 4)) * this.options.animationIntensity * 0.2;
        this.position.y += bounce * 0.01;
    }

    protected updateWaveAnimation(): void {
        const wave = Math.sin(this.animationTime * 2) * this.options.animationIntensity;
        if (this.mainMesh) {
            this.mainMesh.position.y = wave * 0.1;
        }
    }

    protected updateSparkleAnimation(): void {
        if (this.mainMesh) {
            const material = this.mainMesh.material as THREE.MeshBasicMaterial;
            const sparkle = Math.random() > 0.95 ? 1.5 : 1;
            material.opacity = this.options.opacity * sparkle;
        }
    }

    protected updateElectricAnimation(): void {
        const jitter = (Math.random() - 0.5) * this.options.animationIntensity * 0.05;
        this.rotation.z += jitter;
    }

    protected updateBreathingAnimation(): void {
        const breath = 1 + Math.sin(this.animationTime * 1.5) * this.options.animationIntensity * 0.1;
        this.scale.setScalar(this.options.size * breath);
    }

    protected updateStyleAnimation(): void {
        if (!this.effectGroup) return;

        this.effectGroup.children.forEach(child => {
            // Pulsing ring
            if (child.userData.isPulsingRing) {
                const scale = 1 + Math.sin(this.animationTime * 2) * 0.3;
                child.scale.setScalar(scale);
                const mesh = child as THREE.Mesh;
                if (mesh.material && !Array.isArray(mesh.material)) {
                    (mesh.material as THREE.MeshBasicMaterial).opacity = 0.5 - Math.sin(this.animationTime * 2) * 0.3;
                }
            }
        });
    }

    // ====================================================================
    // STATE MANAGEMENT
    // ====================================================================

    protected onStateChange(oldState: ArrowState, newState: ArrowState): void {
        if (!this.mainMesh) return;

        const material = this.mainMesh.material as THREE.MeshBasicMaterial;

        switch (newState) {
            case ArrowState.HOVER:
                material.color.setHex(this.colors.hover);
                this.scale.setScalar(this.options.size * 1.2);
                this.onHoverCallback?.(this);
                break;
            case ArrowState.SELECTED:
                material.color.setHex(this.colors.selected);
                this.scale.setScalar(this.options.size * 1.3);
                this.onSelectCallback?.(this);
                break;
            case ArrowState.DRAGGING:
                material.opacity = 0.7;
                break;
            case ArrowState.DISABLED:
                material.color.setHex(this.colors.disabled);
                material.opacity = 0.5;
                break;
            case ArrowState.IDLE:
            default:
                material.color.setHex(this.colors.primary);
                material.opacity = this.options.opacity;
                this.scale.setScalar(this.options.size);
                break;
        }
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Stil değiştir
     */
    setStyle(style: ArrowStyleType): void {
        this.options.style = style;
        this.build();
    }

    /**
     * Renkler değiştir
     */
    setColors(colors: Partial<ArrowColorPalette>): void {
        this.colors = { ...this.colors, ...colors };

        if (this.mainMesh) {
            (this.mainMesh.material as THREE.MeshBasicMaterial).color.setHex(this.colors.primary);
        }

        if (this.glowShaderMaterial) {
            this.glowShaderMaterial.uniforms.glowColor.value.setHex(this.colors.glow);
        }
    }

    /**
     * Animasyon değiştir
     */
    setAnimation(animation: ArrowAnimation): void {
        this.options.animation = animation;

        if (animation === ArrowAnimation.NONE) {
            this.stopAnimation();
        } else if (this.animationFrame === null) {
            this.startAnimation();
        }
    }

    /**
     * Event callbacks
     */
    onHover(callback: (arrow: FikoInteractiveArrow) => void): void {
        this.onHoverCallback = callback;
    }

    onSelect(callback: (arrow: FikoInteractiveArrow) => void): void {
        this.onSelectCallback = callback;
    }

    onClick(callback: (arrow: FikoInteractiveArrow) => void): void {
        this.onClickCallback = callback;
    }

    // ====================================================================
    // CLEANUP
    // ====================================================================

    /**
     * Geometriyi temizle
     */
    protected clearGeometry(): void {
        if (this.mainMesh) {
            this.remove(this.mainMesh);
            this.mainMesh.geometry?.dispose();
            (this.mainMesh.material as THREE.Material)?.dispose();
            this.mainMesh = null;
        }

        if (this.glowMesh) {
            this.remove(this.glowMesh);
            this.glowMesh.geometry?.dispose();
            this.glowShaderMaterial?.dispose();
            this.glowMesh = null;
            this.glowShaderMaterial = null;
        }

        if (this.outlineMesh) {
            this.remove(this.outlineMesh);
            this.outlineMesh.geometry?.dispose();
            (this.outlineMesh.material as THREE.Material)?.dispose();
            this.outlineMesh = null;
        }

        if (this.effectGroup) {
            this.effectGroup.traverse(child => {
                if (child instanceof THREE.Mesh) {
                    child.geometry?.dispose();
                    (child.material as THREE.Material)?.dispose();
                }
            });
            this.remove(this.effectGroup);
            this.effectGroup = null;
        }
    }

    /**
     * Dispose
     */
    dispose(): void {
        this.stopAnimation();
        this.clearGeometry();
        this.parent?.remove(this);
    }

    /**
     * Static: Cache temizle
     */
    static clearCache(): void {
        FikoInteractiveArrow.geometryCache.forEach(geo => geo.dispose());
        FikoInteractiveArrow.geometryCache.clear();
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

export default FikoInteractiveArrow;
